import React from 'react';
import { motion } from 'framer-motion';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface HealthAnalyticsDashboardDemoProps extends TeaserDemoProps {
  analytics?: {
    totalStrategies: number;
    averageHealthScore: number;
    distribution: {
      excellent: number;
      good: number;
      fair: number;
      poor: number;
    };
    topPerformer: {
      name: string;
      score: number;
      trend: 'up' | 'down';
    };
    portfolioTrend: 'up' | 'down' | 'stable';
  };
}

const HealthAnalyticsDashboardDemoComponent: React.FC<HealthAnalyticsDashboardDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  analytics = {
    totalStrategies: 12,
    averageHealthScore: 87,
    distribution: {
      excellent: 4,
      good: 6,
      fair: 2,
      poor: 0
    },
    topPerformer: {
      name: 'Elite Scalper',
      score: 94,
      trend: 'up'
    },
    portfolioTrend: 'up'
  }
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const getHealthColor = (score: number) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 75) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const getDistributionColor = (category: string) => {
    switch (category) {
      case 'excellent': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'fair': return 'bg-yellow-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return ArrowTrendingUpIcon;
      case 'down': return ArrowTrendingDownIcon;
      default: return ArrowTrendingUpIcon;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-green-400';
      case 'down': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const total = Object.values(analytics.distribution).reduce((a, b) => a + b, 0);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 3, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <TrophyIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Health Analytics</h4>
          <p className="text-gray-300 text-xs">{analytics.totalStrategies} strategies tracked</p>
        </div>
      </div>

      {/* Overall Health Score */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-300 text-sm">Portfolio Health</span>
          <div className="flex items-center gap-1">
            {React.createElement(getTrendIcon(analytics.portfolioTrend), {
              className: `w-4 h-4 ${getTrendColor(analytics.portfolioTrend)}`
            })}
            <motion.span
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`text-2xl font-bold ${getHealthColor(analytics.averageHealthScore)}`}
            >
              {analytics.averageHealthScore}
            </motion.span>
          </div>
        </div>
        <div className="w-full bg-black/30 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${analytics.averageHealthScore}%` }}
            transition={{ duration: 2, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-blue-400 to-green-400 rounded-full"
          />
        </div>
      </div>

      {/* Strategy Distribution */}
      <div className="mb-4">
        <h5 className="text-white text-sm font-medium mb-2">Strategy Distribution</h5>
        <div className="space-y-2">
          {Object.entries(analytics.distribution).map(([category, count], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getDistributionColor(category)}`} />
                <span className="text-gray-300 text-xs capitalize">{category}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 bg-black/30 rounded-full h-1">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(count / total) * 100}%` }}
                    transition={{ delay: index * 0.1 + 0.5, duration: 1 }}
                    className={`h-full rounded-full ${getDistributionColor(category)}`}
                  />
                </div>
                <span className="text-white text-xs font-medium w-4 text-right">{count}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Top Performer */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs">Top Performer</p>
            <p className="text-white font-medium text-sm">{analytics.topPerformer.name}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end mb-1">
              {React.createElement(getTrendIcon(analytics.topPerformer.trend), {
                className: `w-3 h-3 ${getTrendColor(analytics.topPerformer.trend)}`
              })}
              <motion.span
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className={`text-lg font-bold ${getHealthColor(analytics.topPerformer.score)}`}
              >
                {analytics.topPerformer.score}
              </motion.span>
            </div>
            <p className="text-xs text-gray-400">Health Score</p>
          </div>
        </div>
      </motion.div>

      {/* Analytics Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="mt-3 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-purple-300 text-xs font-medium"
        >
          🎯 Real-time health monitoring
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const HealthAnalyticsDashboardDemo = createOptimizedTeaserDemo(
  HealthAnalyticsDashboardDemoComponent,
  'HealthAnalyticsDashboardDemo'
);